import { jsx } from 'react/jsx-runtime';
import styled from '@emotion/styled';
import { LinkType } from '@teko-builder/types/pb';
import { useRef } from 'react';
import { __rest } from 'tslib';

const convertHref = (href, type) => {
    if (type === LinkType.email)
        return `mailto:${href}`;
    if (type === LinkType.phone)
        return `tel:${href}`;
    if (type === LinkType.internal)
        return `/${href}`;
    return href;
};
const LinkV1 = ({ href, target, linkType, children, className, sectionMarginTop = 0, }) => {
    const ref = useRef();
    if (!href)
        return children;
    const scrollToElement = (elementId) => {
        const isInIframe = ref.current.ownerDocument !== document;
        let innerWindow = window;
        if (isInIframe) {
            const iframeElements = window.document.getElementsByTagName('iframe');
            for (const iframe of iframeElements) {
                if (iframe.contentDocument === ref.current.ownerDocument) {
                    innerWindow = iframe.contentWindow;
                    break;
                }
            }
        }
        const elements = ref.current.ownerDocument.querySelectorAll(`[data-id="${elementId}"]`);
        for (const element of elements) {
            if (innerWindow.getComputedStyle(element).display !== 'none') {
                const top = element.getBoundingClientRect().top +
                    innerWindow.pageYOffset -
                    sectionMarginTop;
                innerWindow.scrollTo({
                    top,
                    behavior: 'smooth',
                });
            }
        }
    };
    const computedTarget = () => {
        if (target === '_blank')
            return '_blank';
        if (target === '_self')
            return '_top';
    };
    if (linkType === LinkType.inPage) {
        return (jsx("div", Object.assign({ ref: ref, style: { cursor: 'pointer' }, onClick: () => scrollToElement(href) }, { children: children })));
    }
    return (jsx(Anchor$1, Object.assign({ href: convertHref(href, linkType), target: computedTarget(), className: className, rel: "noopener noreferrer" }, { children: children })));
};
const Anchor$1 = styled.a `
  text-decoration: none;
  color: unset;
  &:hover {
    color: unset;
  }
  cursor: pointer;
`;

const LinkV2 = (_a) => {
    var _b, _c, _d, _e;
    var { eventType, children, className, sectionMarginTop = 0 } = _a, rest = __rest(_a, ["eventType", "children", "className", "sectionMarginTop"]);
    const ref = useRef();
    const openPopup = (popupId) => {
        var _a;
        (_a = ref.current.ownerDocument.getElementById(popupId)) === null || _a === void 0 ? void 0 : _a.click();
    };
    const scrollToElement = (elementId) => {
        const isInIframe = ref.current.ownerDocument !== document;
        let innerWindow = window;
        if (isInIframe) {
            const iframeElements = window.document.getElementsByTagName('iframe');
            for (const iframe of iframeElements) {
                if (iframe.contentDocument === ref.current.ownerDocument) {
                    innerWindow = iframe.contentWindow;
                    break;
                }
            }
        }
        const elements = ref.current.ownerDocument.querySelectorAll(`[data-id="${elementId}"]`);
        for (const element of elements) {
            if (innerWindow.getComputedStyle(element).display !== 'none') {
                const top = element.getBoundingClientRect().top +
                    innerWindow.pageYOffset -
                    sectionMarginTop;
                innerWindow.scrollTo({
                    top,
                    behavior: 'smooth',
                });
            }
        }
    };
    if (eventType === 'link' && ((_b = rest['link']) === null || _b === void 0 ? void 0 : _b.href))
        return (jsx(Anchor, Object.assign({ href: rest['link'].href, target: ((_c = rest[eventType]) === null || _c === void 0 ? void 0 : _c.openNewTab) ? '_blank' : '_top', className: className, rel: "noopener noreferrer" }, { children: children })));
    if (eventType === 'inPage' && rest['inPage'])
        return (jsx("div", Object.assign({ style: { cursor: 'pointer' }, onClick: () => scrollToElement(rest['inPage']), ref: ref }, { children: children })));
    if (eventType === 'internal' && ((_d = rest['internal']) === null || _d === void 0 ? void 0 : _d.href))
        return (jsx(Anchor, Object.assign({ href: `/${rest['internal'].href}`, target: ((_e = rest['internal']) === null || _e === void 0 ? void 0 : _e.openNewTab) ? '_blank' : '_top', className: className, rel: "noopener noreferrer" }, { children: children })));
    if (eventType === 'email' && rest['email'])
        return (jsx(Anchor, Object.assign({ href: `mailto:${rest['email']}`, className: className, rel: "noopener noreferrer", target: "_top" }, { children: children })));
    if (eventType === 'phone' && rest['phone'])
        return (jsx(Anchor, Object.assign({ href: `tel:${rest['phone']}`, className: className, rel: "noopener noreferrer", target: "_top" }, { children: children })));
    if (eventType === 'openPopup' && rest['openPopup']) {
        return (jsx("div", Object.assign({ ref: ref, onClick: () => openPopup(rest['openPopup']) }, { children: children })));
    }
    return children;
};
const Anchor = styled.a `
  text-decoration: none;
  color: unset;
  &:hover {
    color: unset;
  }
  cursor: pointer;
`;

const Link = (props) => {
    if (props.eventType)
        return jsx(LinkV2, Object.assign({}, props));
    return jsx(LinkV1, Object.assign({}, props));
};

export { Link };
